'use client';

import { motion, useInView, useReducedMotion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { AgentActivityMark } from '@/components/chat/agent-activity-mark';
import type { FloatingVoiceDockStatus } from '@/lib/floating-voice-dock-state';

export function VoiceCompanionAvatar({
  status,
  inputMode,
  micMuted,
  pttActive,
}: {
  status: FloatingVoiceDockStatus;
  inputMode: 'vad' | 'ptt';
  micMuted: boolean;
  pttActive: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { amount: 0.5 });
  const reducedMotion = useReducedMotion();
  const [tabVisible, setTabVisible] = useState(true);

  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  const speaking = status === 'speaking';
  const moving = inView && tabVisible && !reducedMotion;
  const listening = status === 'listening';
  const voiceState = micMuted
    ? 'muted'
    : inputMode === 'ptt' && !pttActive && status === 'listening'
      ? 'waiting'
      : status;

  return (
    <span
      ref={ref}
      data-slot="voice-companion-face"
      data-voice-state={voiceState}
      className="relative flex size-13 items-center justify-center"
      aria-hidden="true"
    >
      <motion.span
        className="absolute inset-1 rounded-full border border-violet-500/30"
        animate={
          moving && speaking
            ? { scale: [0.9, 1.18, 0.9], opacity: [0.2, 0.65, 0.2] }
            : { scale: 1, opacity: 0 }
        }
        transition={
          moving && speaking
            ? { duration: 1.15, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.15 }
        }
      />
      <motion.span
        className="relative flex size-13 items-center justify-center drop-shadow-sm"
        animate={
          moving && speaking
            ? { scale: [1, 1.08, 0.97, 1.06, 1] }
            : { scale: 1 }
        }
        transition={
          moving && speaking
            ? { duration: 0.85, repeat: Infinity, ease: 'easeInOut' }
            : { duration: 0.15 }
        }
      >
        <AgentActivityMark
          state={listening ? 'thinking' : 'awaiting'}
          animate={listening}
          activeFill
          activePalette={listening ? 'green' : 'multicolor'}
          staticShape={listening ? 'cloud' : speaking ? 'circle' : 'square'}
          staticExpression={
            listening ? 'curious' : speaking ? 'excited' : 'attentive'
          }
          className="size-13"
        />
      </motion.span>
      {speaking && (
        <motion.svg
          viewBox="0 0 52 52"
          className="pointer-events-none absolute inset-0 size-13 overflow-visible text-violet-600 dark:text-violet-300"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          aria-hidden="true"
          animate={
            moving
              ? { opacity: [0.25, 0.8, 0.25], scale: [0.96, 1.08, 0.96] }
              : { opacity: 0.7, scale: 1 }
          }
          transition={
            moving
              ? { duration: 0.85, repeat: Infinity, ease: 'easeInOut' }
              : { duration: 0 }
          }
        >
          <path d="M4 18c-2 4-2 12 0 16M48 18c2 4 2 12 0 16" />
        </motion.svg>
      )}
    </span>
  );
}
